import { FunctionComponent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

type VerificationStatus = "success" | "already_verified" | "expired" | "invalid";

const EmailVerificationResult: FunctionComponent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const status = (searchParams.get("status") as VerificationStatus) || "invalid";
  const message = searchParams.get("message") || "Unknown error occurred";
  const email = searchParams.get("email");

  const getStatusConfig = () => {
    switch (status) {
      case "success":
        return {
          title: "Email Verified Successfully!",
          icon: (
            <svg
              className="w-16 h-16 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          textColor: "text-green-700",
        };
      case "already_verified":
        return {
          title: "Email Already Verified",
          icon: null,
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
          textColor: "text-blue-700",
        };
      case "expired":
        return {
          title: "Verification Link Expired",
          icon: (
            <svg
              className="w-16 h-16 text-orange-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
          bgColor: "bg-orange-50",
          borderColor: "border-orange-200",
          textColor: "text-orange-700",
        };
      case "invalid":
      default:
        return {
          title: "Invalid Verification Link",
          icon: (
            <svg
              className="w-16 h-16 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          ),
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          textColor: "text-red-700",
        };
    }
  };

  const config = getStatusConfig();
  const showResendOption = status === "expired" || status === "invalid";
  const showLoginButton = status === "success" || status === "already_verified";

  const handleResendEmail = async () => {
    if (!email) {
      navigate("/sign-in");
      return;
    }

    setIsResending(true);
    setResendError(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";
      const response = await fetch(
        `${apiUrl}/auth/send-verification-email-again`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to resend verification email");
      }

      setResendSuccess(true);
    } catch (error) {
      setResendError(
        error instanceof Error ? error.message : "Failed to resend verification email"
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="w-full h-screen flex flex-col items-center justify-center bg-background-grey">
      <div
        className={`bg-white p-10 rounded-lg shadow-lg text-center max-w-md ${config.bgColor} border ${config.borderColor}`}
      >
        <div className="flex justify-center mb-4">{config.icon}</div>
        <h1 className={`text-2xl font-semibold mb-4 ${config.textColor}`}>
          {config.title}
        </h1>
        <p className="mb-6 text-gray-700">{decodeURIComponent(message)}</p>

        {resendSuccess && (
          <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded-lg text-green-700">
            Verification email sent! Please check your inbox.
          </div>
        )}

        {resendError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700">
            {resendError}
          </div>
        )}

        {showLoginButton && (
          <button
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition w-full"
            onClick={() => navigate("/sign-in")}
          >
            Go to Sign In
          </button>
        )}

        {showResendOption && !resendSuccess && (
          <div className="space-y-3">
            {email && (
              <button
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition w-full disabled:bg-blue-300"
                onClick={handleResendEmail}
                disabled={isResending}
              >
                {isResending ? "Sending..." : "Resend Verification Email"}
              </button>
            )}
            <button
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition w-full"
              onClick={() => navigate("/sign-in")}
            >
              Back to Sign In
            </button>
          </div>
        )}

        {showResendOption && resendSuccess && (
          <button
            className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition w-full"
            onClick={() => navigate("/sign-in")}
          >
            Back to Sign In
          </button>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationResult;
